import queryClient from '../../../ReactQuery/client';
import useMutation from '../../../ReactQuery/useMutation';
import { put } from '../../../redux/api';
import type { Banner, GetBannersResponse, UpdateBannerRequest } from '../../../../shared/types/routes/banners';

import { invalidateActiveBanners } from './useActiveBanners';
import { bannersQueryKey } from './useBanners';

type UpdateBannerVariables = {
  id: number;
  banner: UpdateBannerRequest;
};

const updateBanner = ({ id, banner }: UpdateBannerVariables) =>
  put<UpdateBannerRequest, Banner>(`/api/banners/${id}`, banner);

/**
 * Hook for updating an existing banner.
 */
export default function useUpdateBanner() {
  return useMutation<UpdateBannerVariables, Response & { data?: Banner }>({
    mutationFn: updateBanner,
    onSuccess: (response, { id, banner }: UpdateBannerVariables) => {
      const updated = response?.data ?? null;
      // Replace the cached banner with the server response so scope, placement,
      // and scopedOrganizations stay in sync with what was actually persisted. If the server
      // didn't echo the row, merge the request payload so the UI still reflects the edit.
      queryClient.setQueryData<GetBannersResponse>(bannersQueryKey, (banners?: GetBannersResponse) => {
        if (!banners) {
          return [] as GetBannersResponse;
        }
        return banners.map((cachedBanner) => {
          if (cachedBanner.id !== id) {
            return cachedBanner;
          }
          return updated ?? { ...cachedBanner, ...banner };
        }) as GetBannersResponse;
      });
      // Active banner caches are placement-keyed; invalidate so updated scope/placement propagates.
      void invalidateActiveBanners();
    },
  });
}
